import React, { useState, useContext, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from 'context/auth/auth_context';
import axiosInstance from 'utils/axios-instance';
import Swal from 'sweetalert2';
import './index.css';
import MemberManagement from './members';
import NoticeManagement from './notices';
import AdminRequestManagement from './requests';

type Community = {
    uuid: string;
    name: string;
    description: string;
    type: string;
    geoFeaturesName: string;
}

const AdminPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const authContext = useContext(AuthContext);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [loading, setLoading] = useState(true);
    const [community, setCommunity] = useState<Community | null>(null);
    const [activeTab, setActiveTab] = useState<'members' | 'notices' | 'requests'>('notices');

    useEffect(() => {
        if (!authContext.isLoaded) {
            return;
        }
        setIsAuthenticated(authContext.getIsAuthenticated());
    }, [authContext.isLoaded, authContext]);

    useEffect(() => {
        if (isAuthenticated) {
            checkAdminAndLoadData();
        } else {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, id]);

    const checkAdminAndLoadData = async () => {
        try {
            setLoading(true);

            // 관리자 권한 확인
            const adminCheckResponse = await axiosInstance.get(
                `/api/community/admin/check?communityUuid=${id}`
            );
            
            if (!adminCheckResponse.data) {
                // 관리자가 아니면 관리자 신청 페이지로 이동
                navigate(`/community/${id}/settings/request`, { replace: true });
                return;
            }

            setIsAdmin(true);

            // 커뮤니티 정보 조회 (건물/동네 자동 판별)
            let communityResponse;
            try {
                communityResponse = await axiosInstance.get(`/api/community/building?uuid=${id}`);
            } catch {
                communityResponse = await axiosInstance.get(`/api/community/town?uuid=${id}`);
            }
            if (communityResponse.data.statusCode === 200) {
                setCommunity(communityResponse.data.community);
            }
        } catch (error) {
            console.error('관리자 권한 확인 실패:', error);
            Swal.fire({
                icon: 'error',
                title: '오류',
                text: '관리자 권한 확인 중 오류가 발생했습니다.',
            }).then(() => {
                navigate(`/community/building/${id}`);
            });
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="admin-page">
                <div className="loading-container">
                    <div className="loader"></div>
                    <p>로딩 중...</p>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return (
            <div className="admin-page">
                <div className="request-container">
                    <div className="request-header">
                        <h1>커뮤니티 관리</h1>
                    </div>
                    <div className="request-info-box">
                        <h3>📋 로그인이 필요합니다</h3>
                        <p>관리자 페이지를 이용하려면 로그인이 필요합니다.</p>
                    </div>
                    <div className="form-actions">
                        <button 
                            className="submit-button"
                            onClick={() => navigate('/auth/signin', {
                                state: {
                                    from: `/community/${id}/settings`,
                                    fromfrom: `/community/${id}/settings`
                                }
                            })}
                        >
                            로그인하기
                        </button>
                        <button 
                            className="cancel-button"
                            onClick={() => navigate(`/community/building/${id}`)}
                        >
                            취소
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!isAdmin) {
        return null; // 이미 리다이렉트 중
    }

    return (
        <div className="admin-page">
            <div className="admin-header">
                <div className="header-content">
                    <h1>커뮤니티 관리</h1>
                    {community && (
                        <p className="community-name">{community.name} ({community.geoFeaturesName})</p>
                    )}
                </div>
                <button 
                    className="back-button"
                    onClick={() => navigate(`/community/building/${id}`)}
                >
                    커뮤니티로 돌아가기
                </button>
            </div>

            <div className="admin-container">
                <div className="tab-navigation">
                    <button 
                        className={`tab-button ${activeTab === 'notices' ? 'active' : ''}`}
                        onClick={() => setActiveTab('notices')}
                    >
                        공지사항 관리
                    </button>
                    <button 
                        className={`tab-button ${activeTab === 'members' ? 'active' : ''}`}
                        onClick={() => setActiveTab('members')}
                    >
                        회원 관리
                    </button>
                    <button 
                        className={`tab-button ${activeTab === 'requests' ? 'active' : ''}`}
                        onClick={() => setActiveTab('requests')}
                    >
                        관리자 신청
                    </button>
                </div>

                <div className="tab-content">
                    {activeTab === 'notices' && <NoticeManagement communityUuid={id!} />}
                    {activeTab === 'members' && <MemberManagement communityUuid={id!} />}
                    {activeTab === 'requests' && <AdminRequestManagement communityUuid={id!} />}
                </div>
            </div>
        </div>
    );
};

export default AdminPage;

